import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { EmptyState, Section } from "@/components/common";
import { scoreTone } from "@/lib/format";
import { cn } from "@/lib/utils";

export const DIMS = [
  ["compliance", "Compliance & accuracy"],
  ["discovery", "Discovery"],
  ["solution_framing", "Solution framing"],
  ["closing_next_steps", "Closing & next steps"],
  ["empathy_tone", "Empathy & tone"],
];

export function ScoreHistory({ series, threshold, className }) {
  const scored = (series || []).filter((s) => s.avg_score != null);
  return (
    <Section title="Score history" eyebrow="Average coaching score per period · bars = calls" className={className} testId="scorecard-history">
      {scored.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={series} margin={{ left: -10, right: 10, top: 10 }}>
            <CartesianGrid stroke="#1f293d" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
            <YAxis yAxisId="l" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fill: "#10b981", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#111827", border: "1px solid #2d3d58" }} labelStyle={{ color: "#94a3b8" }} />
            <ReferenceLine yAxisId="r" y={threshold} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `threshold ${threshold}`, fill: "#ef4444", fontSize: 10, position: "insideTopRight" }} />
            <Bar yAxisId="l" dataKey="calls" fill="#3b82f6" fillOpacity={0.35} radius={[3, 3, 0, 0]} name="Calls" barSize={10} />
            <Bar yAxisId="l" dataKey="analyzed" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="AI reviewed" barSize={10} />
            <Line yAxisId="r" type="monotone" dataKey="avg_score" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} connectNulls name="Avg score" />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState title="No scored calls in this range" hint="Scores appear once GPT-5.4 reviews this agent's calls." />
      )}
    </Section>
  );
}

export function DimensionCompare({ stats, team }) {
  const has = DIMS.some(([k]) => stats?.[k] != null);
  return (
    <Section title="Dimension breakdown" eyebrow="Agent vs team average" testId="scorecard-dimensions">
      {has ? (
        <div className="space-y-3">
          {DIMS.map(([k, label]) => {
            const v = stats?.[k];
            const t = team?.[k];
            const tone = scoreTone(v);
            const diff = v != null && t != null ? Math.round((v - t) * 10) / 10 : null;
            return (
              <div key={k} data-testid={`scorecard-dim-${k}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-300">{label}</span>
                  <span className="font-mono"><span className={cn("font-bold", tone.text)}>{v ?? "—"}</span><span className="text-slate-500"> · team {t ?? "—"}</span>{diff != null && <span className={cn("ml-1.5", diff >= 0 ? "text-emerald-400" : "text-red-400")}>{diff >= 0 ? "+" : ""}{diff}</span>}</span>
                </div>
                <div className="relative h-2 bg-[#161f30] rounded">
                  <div className="h-2 rounded transition-[width] duration-500" style={{ width: `${v || 0}%`, background: tone.hex }} />
                  {t != null && <div className="absolute top-[-3px] h-[14px] w-[2px] bg-slate-300 rounded" style={{ left: `${t}%` }} title={`Team ${t}`} />}
                </div>
              </div>
            );
          })}
          <div className="text-[10px] text-slate-500">White tick = team average for the same period.</div>
        </div>
      ) : (
        <EmptyState title="No dimension scores yet" />
      )}
    </Section>
  );
}
